<template>
  <Obj :id="id" :value="value">
    <RecurrentTask
      v-if="value"
      ref="component"
      :value="value"
      :embedded-mode="embeddedMode"
    />
  </Obj>
</template>

<script lang="ts">
import { isEqual, map } from "lodash-es";
import { defineComponent, onMounted, onUpdated, toRefs, watch } from "vue";
import { useRoute } from "vue-router";

import type { RecurrentTask } from "@ctrl/controller-rpc";

import * as inject from "@/components/inject";
import Component from "@/components/recurrent-task/RecurrentTask.vue";
import { bindAsync, useReleaser } from "@/ref";

import Obj from "./DefaultObject.vue";
import { useObject } from "./object";

export default defineComponent({
  components: {
    Obj,
    RecurrentTask: Component,
  },
  props: {
    id: { type: String, required: true },
    embeddedMode: {
      type: Boolean,
      default: false,
    },
  },
  async setup(props) {
    const { id } = toRefs(props);
    const route = useRoute();
    const controller = inject.controller();
    const {
      initializeComponentFocus,
      installRouteCallbacks,
      release,
      setObjectValue,
      ...object
    } = useObject(route, (value: RecurrentTask) =>
      map(
        ["notes", "title"] satisfies Array<keyof RecurrentTask>,
        (prop: keyof RecurrentTask) =>
          watch(
            () => value![prop],
            (v, previous) => {
              if (!isEqual(v, previous))
                controller.taskRecurrent.update(
                  value.id,
                  {
                    [prop]: v,
                  },
                  "app"
                );
            }
          )
      )
    );
    installRouteCallbacks();
    onMounted(initializeComponentFocus);
    onUpdated(initializeComponentFocus);
    const releaser = useReleaser();
    releaser.defer(release);
    const task = await releaser.add(
      bindAsync(id, async (id) => {
        const t = await controller.taskRecurrent.get(id);
        return t.map((t) => (t ? { ...t, type: "task-rec" } : t));
      })
    );
    setObjectValue(task);
    return object;
  },
});
</script>
